import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Routes } from 'react-router-dom';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import AppTabs from '../../components/designe/app-tabs/AppTabs';
import AppHelpTooltipButton from '../../components/designe/tooltips/AppHelpTooltipButton';
import PageContainer from '../../components/layout/page-container/PageContainer';
import AppNavigate from '../../components/reusable/app-navigate/AppNavigate';
import ChoosePlanDialog from '../../components/reusable/choose-plan-dialog/ChoosePlanDialog';
import { useUserConfig } from '../../context/UserConfigContext';
import { usePullRefreshInvalidateTags } from '../../hooks/usePullRefreshInvalidateTags';
import { programsApi } from '../../redux/api/programsAPI';
import { PROGRAMS } from '../../redux/constants';
import { Paths } from '../../shared/paths';
import ProgramTooltipContent from './ProgramTooltipContent';
import { useStyles } from './Programs.styles';
import AvailableProgramItems from './available-program-items/AvailableProgramItems';
import ProgramCollections from './program-collections/ProgramCollections';
import ProgramItems from './program-items/ProgramItems';
import ProgramSets from './program-sets/ProgramSets';

const Programs = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const { options } = useUserConfig();
  const [planDialogOpened, setPlanDialogOpen] = useState(false);
  const { onRefresh } = usePullRefreshInvalidateTags([
    {
      apiSlice: programsApi,
      tags: [
        PROGRAMS.PROGRAMS,
        PROGRAMS.PROGRAM_ITEMS,
        PROGRAMS.PROGRAM_SETS,
        PROGRAMS.AVAILABLE,
      ],
    },
  ]);

  const tabsConfig = useMemo(
    () =>
      [
        {
          label: t('programs.myItems'),
          path: getListPath(Paths.programs.item),
          onboardingKey: 'programPage.tab.items',
        },
        {
          label: t('programs.programSets'),
          path: getListPath(Paths.programs.set),
          onboardingKey: 'programPage.tab.sets',
        },
        {
          label: t('programs.collections'),
          path: getListPath(Paths.programs.collection),
          onboardingKey: 'programPage.tab.collections',
        },
        {
          label: t('programs.availableToMePrograms'),
          path: getListPath(Paths.programs.available),
          onboardingKey: 'programPage.tab.available',
        },
      ].filter(Boolean),
    [i18n.language, t],
  );

  function getListPath(path: string) {
    return `${Paths.programs.toList}${path}`;
  }

  const showTooltips = options.showTooltips;
  const pageHeaderTooltip = showTooltips ? (
    <Box className={classes.headerTooltip}>
      <AppHelpTooltipButton
        placement="bottom-end"
        scrollableContent
        content={<ProgramTooltipContent t={t} />}
      />
    </Box>
  ) : null;

  return (
    <PageContainer
      title={t('programs.programs')}
      description={t('programs.programsDescription')}
      headerContent={pageHeaderTooltip}
      onRefresh={onRefresh}>
      <div className={classes.programsWrapper}>
        <AppTabs config={tabsConfig} />
        <div className={classes.tabsContent}>
          <Routes>
            <Route
              path={Paths.programs.item}
              element={<ProgramItems setPlanDialogOpen={setPlanDialogOpen} />}
            />
            <Route
              path={Paths.programs.collection}
              element={
                <ProgramCollections setPlanDialogOpen={setPlanDialogOpen} />
              }
            />
            <Route path={Paths.programs.set} element={<ProgramSets />} />
            <Route
              path={Paths.programs.available}
              element={<AvailableProgramItems />}
            />
            <Route
              path={'*'}
              element={<AppNavigate to={Paths.programs.toItem} replace />}
            />
          </Routes>
        </div>
      </div>
      <ChoosePlanDialog
        open={planDialogOpened}
        onClose={() => setPlanDialogOpen(false)}
        variant="subscriptionLimit"
      />
    </PageContainer>
  );
};

export default Programs;
